// ─── STANDALONE SUPABASE ASYNC FUNCTIONS ─────────────────────────────────────
import { supabase } from "./supabaseClient";

// ─── SHARED: fetchThirtyDayLogs ───────────────────────────────────────────────
// Queries the logs table directly for up to 30 days — source of truth for
// Progress tab and WeightVsCalorieChart. Avoids dayHistory stale/slice issues.
export async function fetchThirtyDayLogs(userId) {
  const dates = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - i);
    return [d.getFullYear(), String(d.getMonth()+1).padStart(2,"0"), String(d.getDate()).padStart(2,"0")].join("-");
  });
  const oldest = dates[dates.length - 1];

  // Fetch food logs and water logs in parallel
  // water_logs query is optional — fails gracefully if table not yet created
  const [foodRes, waterRes] = await Promise.all([
    supabase.from("logs").select("log_date,calories,protein,carbs,fat,fibre,sugar,sodium")
      .eq("user_id", userId).gte("log_date", oldest).order("log_date", { ascending: false }),
    supabase.from("water_logs").select("log_date,water_ml")
      .eq("user_id", userId).gte("log_date", oldest)
      .then(r => r).catch(() => ({ data: [], error: null }))
  ]);
  if (foodRes.error) throw foodRes.error;
  // Suppress water_logs errors — table may not exist yet
  if (waterRes.error && process.env.NODE_ENV !== "production") { console.warn("[fetchThirtyDayLogs] water_logs unavailable:", waterRes.error.message); }

  const byDay = {};
  dates.forEach(d => { byDay[d] = { calories:0, protein:0, carbs:0, fat:0, fibre:0, sugar:0, sodium:0, water:0, logged:false }; });

  (foodRes.data || []).forEach(r => {
    if (!byDay[r.log_date]) return;
    byDay[r.log_date].calories += Math.round(parseFloat(r.calories) || 0);
    byDay[r.log_date].protein  += parseFloat(r.protein)  || 0;
    byDay[r.log_date].carbs    += parseFloat(r.carbs)    || 0;
    byDay[r.log_date].fat      += parseFloat(r.fat)      || 0;
    byDay[r.log_date].fibre    += parseFloat(r.fibre)    || 0;
    byDay[r.log_date].sugar    += parseFloat(r.sugar)    || 0;
    byDay[r.log_date].sodium   += parseFloat(r.sodium)   || 0;
    byDay[r.log_date].logged    = true;
  });

  // Merge water — water_logs has one authoritative row per day
  (waterRes.data || []).forEach(r => {
    if (byDay[r.log_date] !== undefined) byDay[r.log_date].water = parseFloat(r.water_ml) || 0;
  });

  return dates.slice().reverse().map(d => ({ date: d, ...byDay[d] }));
}

// ─── SHARED: fetchSevenDayLogs ────────────────────────────────────────────────
// Single source of 7-day log aggregation — used by both loadIntelMeta() and
// runWeeklyBriefing(). Eliminates duplicated Supabase queries.
// Returns { byDay, dates }
//   byDay:  { "YYYY-MM-DD": { calories, protein, carbs, fat, logged } }
//   dates:  [today, yesterday, ..., 6-days-ago]  (index 0 = today)
export async function fetchSevenDayLogs(userId) {
  const dates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - i);
    return [d.getFullYear(), String(d.getMonth()+1).padStart(2,"0"), String(d.getDate()).padStart(2,"0")].join("-");
  });
  const oldest = dates[dates.length - 1];
  // Fetch food logs and water_logs in parallel — same pattern as fetchThirtyDayLogs
  const [foodRes, waterRes] = await Promise.all([
    supabase.from("logs")
      .select("log_date,calories,protein,carbs,fat,fibre,logged_at")
      .eq("user_id", userId).gte("log_date", oldest)
      .order("logged_at", { ascending: true }),
    supabase.from("water_logs")
      .select("log_date,water_ml")
      .eq("user_id", userId).gte("log_date", oldest),
  ]);
  if (foodRes.error) throw foodRes.error;
  if (waterRes.error && process.env.NODE_ENV !== "production") { console.warn("[fetchSevenDayLogs] water_logs unavailable:", waterRes.error.message); }
  const byDay = {};
  dates.forEach(d => { byDay[d] = { calories:0, protein:0, carbs:0, fat:0, fibre:0, sugar:0, sodium:0, water:0, logged:false }; });
  (foodRes.data || []).forEach(r => {
    if (!byDay[r.log_date]) return;
    byDay[r.log_date].calories += parseFloat(r.calories) || 0;
    byDay[r.log_date].protein  += parseFloat(r.protein)  || 0;
    byDay[r.log_date].carbs    += parseFloat(r.carbs)    || 0;
    byDay[r.log_date].fat      += parseFloat(r.fat)      || 0;
    byDay[r.log_date].fibre    += parseFloat(r.fibre)    || 0;
    byDay[r.log_date].logged    = true;
  });
  // Merge water — water_logs has one authoritative row per day
  (waterRes.data || []).forEach(r => {
    if (byDay[r.log_date] !== undefined) byDay[r.log_date].water = parseFloat(r.water_ml) || 0;
  });
  return { byDay, dates };
}

// ─── fetchKnownBrands ─────────────────────────────────────────────────────────
// Returns a Set of lowercase brand names from the branded_foods table.
// Called once on mount so any new brand added to the DB is picked up automatically
// without requiring a code change.
export async function fetchKnownBrands() {
  try {
    const { data } = await supabase
      .from("branded_foods")
      .select("brand")
      .order("brand", { ascending: true });
    if (!data) return new Set();
    return new Set(data.map(r => r.brand.trim().toLowerCase()));
  } catch(_) {
    return new Set();
  }
}
